import React, { type PropsWithChildren, useCallback } from 'react';
import type { FieldInputProps, FieldRenderProps } from 'react-final-form';

import Checkbox, { type CheckboxProps } from '../components/checkbox/checkbox';
import CheckboxCard, { type CheckboxCardProps } from '../components/checkbox-card/checkbox-card';
import Input, { type InputProps } from '../components/input/input';
import Textarea, { type TextareaProps } from '../components/input/textarea';
import InputRange from '../components/input-range/input-range';
import RadioBlock, { type RadioBlockProps } from '../components/radio-block/radio-block';
import Select, { type SelectProps } from '../components/select/select';
import Switch, { type SwitchProps } from '../components/switch/switch';
import { type AdapterOptions, setChangeHandler } from '../utils/form';

type InputAdapterProps = FieldInputProps<HTMLInputElement> &
  InputProps & {
    showsErrorsOnMount?: boolean;
  };

export const InputAdapter = React.forwardRef<HTMLInputElement, InputAdapterProps>(
  ({ input, meta, showsErrorsOnMount, adapterOptions, ...rest }, ref) => {
    const onChange = setChangeHandler(input.onChange, adapterOptions);
    return (
      <Input
        {...input}
        {...rest}
        error={(meta.touched || showsErrorsOnMount) && meta.error ? meta.error : undefined}
        onChange={onChange}
        ref={ref}
      />
    );
  },
);

type InputRangeAdapterProps = FieldInputProps<HTMLInputElement> & {
  showsErrorsOnMount?: boolean;
};

export const InputRangeAdapter = React.forwardRef<HTMLInputElement, InputRangeAdapterProps>(
  ({ input, meta, showsErrorsOnMount, adapterOptions, ...rest }, ref) => (
    <InputRange
      {...input}
      {...rest}
      error={(meta.touched || showsErrorsOnMount) && meta.error ? meta.error : undefined}
      ref={ref}
    />
  ),
);

export type TextareaAdapterProps = FieldRenderProps<string> & TextareaProps;

export const TextareaAdapter = ({ input, meta, ...rest }: TextareaAdapterProps) => {
  const onChange = setChangeHandler(input.onChange);
  return (
    <Textarea {...input} {...rest} error={meta.touched && meta.error ? meta.error : undefined} onChange={onChange} />
  );
};

type RadioBlockAdapterProps = FieldRenderProps<string> &
  RadioBlockProps &
  PropsWithChildren<{
    onChangeCallback?: () => void;
  }>;

export const RadioBlockAdapter = ({ input, meta, children, onChangeCallback, ...rest }: RadioBlockAdapterProps) => {
  const onChange = useCallback(
    // @ts-ignore TODO: set typing
    (event) => {
      setChangeHandler(input.onChange)(event);
      if (onChangeCallback) onChangeCallback();
    },
    [input.onChange, onChangeCallback],
  );

  return (
    <RadioBlock {...input} {...rest} onChange={onChange}>
      {children}
    </RadioBlock>
  );
};

type CheckboxCardAdapterProps = FieldRenderProps<string> & CheckboxCardProps;

export const CheckboxCardAdapter = ({ input, meta, children, ...rest }: CheckboxCardAdapterProps) => (
  <CheckboxCard {...input} {...rest} onChange={input.onChange} checked={!!input.checked}>
    {children}
  </CheckboxCard>
);

type CheckboxAdapterProps = FieldRenderProps<string> &
  CheckboxProps & {
    adapterOptions?: AdapterOptions;
  };

export const CheckboxAdapter = ({ input, meta, ...rest }: CheckboxAdapterProps) => (
  <Checkbox {...input} {...rest} onChange={input.onChange} checked={!!input.checked} />
);

type SwitchAdapterProps = FieldRenderProps<string> &
  SwitchProps & {
    onChange?: SwitchProps['onChange'];
  };

export const SwitchAdapter = ({ input, meta, onChange, ...rest }: SwitchAdapterProps) => {
  const handleOnChange = useCallback(
    // @ts-ignore TODO: set typing
    (event) => {
      setChangeHandler(input.onChange)(event);
      if (onChange) onChange(event);
      return input.onChange(event);
    },
    [input, onChange],
  );

  return <Switch {...input} {...rest} onChange={handleOnChange} isChecked={!!input.isChecked} />;
};

type SelectAdapterProps = FieldInputProps<HTMLSelectElement> & {
  onChange?: SelectProps['onChange'];
};

export const SelectAdapter = React.forwardRef<HTMLSelectElement, SelectAdapterProps>(
  ({ input, meta, onChange, ...rest }, ref) => {
    const onChangeCallback = useCallback(
      // @ts-ignore TODO: set typing
      (event) => {
        setChangeHandler(input.onChange)(event);
        if (onChange) onChange(event);
      },
      [input.onChange, onChange],
    );

    return (
      <Select
        {...input}
        {...rest}
        /*
         * meta.touched does not seem to change to "true" for select, unlike input.
         * meta.modified is used instead.
         */
        error={(meta.touched || meta.modified) && meta.error ? meta.error : undefined}
        onChange={onChangeCallback}
        ref={ref}
      />
    );
  },
);
